import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { ChevronRight, ExternalLink } from "lucide-mui";
import type { KeyboardEvent, ReactNode, SyntheticEvent } from "react";
import type { ProspectListContactEntry } from "../../api/client.ts";
import UserAvatar from "../UserAvatar.tsx";

// Labels the contact research agent sets on each person, in display order.
const CONTACT_LABELS = ["Seniority", "Priority", "Source"];

// Clicks inside a card's own controls stay theirs. Keys need no guard: the
// card only takes Enter / Space aimed at itself, and the page's shortcuts
// must still hear the rest.
const keep = { onClick: (e: SyntheticEvent) => e.stopPropagation() };

interface ContactCardProps {
  contact: ProspectListContactEntry;
  /** Its drawer is open: the ring stays on while it is. */
  selected?: boolean;
  /** Opens the person; `el` is the card, where the drawer's colour floods from. */
  onOpen: (el: HTMLElement) => void;
  /** Right of the name: a review toggle, a kebab. */
  controls?: ReactNode;
  /** Under the card's lines: a note, CRM state. */
  footer?: ReactNode;
}

/**
 * A person in a drawer: avatar, name, title, the agent's labels and how to
 * reach them. The whole card opens the person in a `ContactDrawer` beside it;
 * its controls and footer keep their own clicks. A grey filled box at rest;
 * the open one takes the people colour its drawer floods with.
 */
export default function ContactCard({ contact: c, selected = false, onOpen, controls, footer }: ContactCardProps) {
  const name = c.contact_name ?? "Unnamed contact";
  const caption = [c.title ?? c.labels.Role, c.headline].filter(Boolean).join(" · ");
  const reach = [c.reachability.email, c.reachability.phone].filter(Boolean).join(" · ");
  const labels = CONTACT_LABELS.filter((k) => c.labels[k]);
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget || (e.key !== "Enter" && e.key !== " ")) return;
    e.preventDefault();
    onOpen(e.currentTarget);
  };
  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label={`Open ${name}`}
      aria-expanded={selected}
      data-contact-entry={c.id}
      onClick={(e) => onOpen(e.currentTarget)}
      onKeyDown={onKeyDown}
      sx={{
        p: 1.5,
        borderRadius: 2,
        // Filled, never outlined: grey at rest, the people colour while open.
        bgcolor: selected ? "people.light" : "action.hover",
        cursor: "pointer",
        outline: "none",
        transition: "background-color 200ms, box-shadow 150ms, transform 120ms",
        "&:hover": { bgcolor: selected ? "people.light" : "action.selected" },
        "&:hover .contact-card-chevron, &[aria-expanded='true'] .contact-card-chevron": {
          opacity: 1,
          transform: "translateX(2px)",
        },
        "&:focus-visible": { boxShadow: "0 0 0 2px var(--mui-palette-people-main)" },
        "&:active": { transform: "scale(0.995)" },
        "@media (prefers-reduced-motion: reduce)": { transition: "none", "&:active": { transform: "none" } },
      }}
    >
      <Stack direction="row" spacing={1.25} sx={{ alignItems: "center" }}>
        <UserAvatar displayName={c.contact_name} pictureUrl={c.photo_url} size={32} tooltip={false} />
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
            {name}
          </Typography>
          <Typography variant="caption" noWrap component="div" sx={{ color: "text.secondary" }}>
            {caption || "No title on record"}
          </Typography>
        </Box>
        {controls && (
          <Stack direction="row" spacing={0.5} sx={{ alignItems: "center", flexShrink: 0 }} {...keep}>
            {controls}
          </Stack>
        )}
        <ChevronRight
          className="contact-card-chevron"
          fontSize="small"
          sx={{
            color: selected ? "people.dark" : "text.secondary",
            opacity: 0.35,
            flexShrink: 0,
            transition: "opacity 150ms, transform 150ms",
          }}
        />
      </Stack>
      <Stack direction="row" spacing={0.75} useFlexGap sx={{ alignItems: "center", flexWrap: "wrap", mt: 1, pl: 5.25 }}>
        <Typography variant="caption" noWrap sx={{ color: reach ? "text.primary" : "text.secondary", minWidth: 0 }}>
          {reach || "No email or phone yet"}
        </Typography>
        {c.reachability.linkedin_url && (
          <Tooltip title="LinkedIn profile">
            <IconButton
              size="small"
              component="a"
              href={c.reachability.linkedin_url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn profile"
              sx={{ p: 0.25, color: "text.secondary" }}
              {...keep}
            >
              <ExternalLink sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}
        {labels.map((k) => (
          <Chip
            key={k}
            size="small"
            variant="outlined"
            label={`${k}: ${c.labels[k]}`}
            sx={{ height: 20, bgcolor: "background.paper" }}
          />
        ))}
      </Stack>
      {footer && (
        <Box sx={{ mt: 1, pl: 5.25 }} {...keep}>
          {footer}
        </Box>
      )}
    </Box>
  );
}
